import {
  Component,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";
import { MatRowDef, MatTableModule } from "@angular/material/table";
import { MatDialogModule, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatSelectModule } from "@angular/material/select";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatOptionModule } from "@angular/material/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatExpansionModule } from "@angular/material/expansion";
import { LocationOneService } from "app/modules/components/locations/location-one/location-one.service.service";
import { MatMenuModule } from "@angular/material/menu";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { ICreateLocationOne } from "./interfaces/create-location-one.interface";
import { LocationOneAttributes } from "@albatrosdeveloper/ave-models-package";
import { IUpdateLocationOne } from "./interfaces/update-location-one.interface";
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { ExportService } from "app/layout/export-overlay/export-overlay.service";
import { Subscription } from "rxjs";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { StatusFiltersComponent } from "../../filters/status-filters/status-filters.component";
import { isNumber } from "lodash";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";


@Component({
  selector: "app-location-one",
  templateUrl: "./location-one.component.html",
  styleUrls: ["./location-one.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    StatusFiltersComponent,
    HoverMenuDirective
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})
export class LocationOneComponent implements AfterViewInit, OnDestroy {
  displayedColumns: string[] = ["nombre", "estado", "acciones"];
  currentCreateLocation: ICreateLocationOne = this.initializeCreateLocation();
  currentUpdateLocation: LocationOneAttributes | undefined;

  dataSource: LocationOneAttributes[] = [];
  searchQuery:string = '';
  totalItems:number = 0;
  pageSize:number = 50;
  pageIndex:number = 0;

  dialogRefAdd: MatDialogRef<any>;
  dialogRefEdit:MatDialogRef<any>;

  @ViewChild("editModal") editModal!: TemplateRef<any>;
  @ViewChild("addModal") addModal!: TemplateRef<any>;
  @ViewChild("deleteDialog") deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //BUSCADOR
  searchSubscription: Subscription;

  selectedStatuses: number[] = [0,1];
  
  constructor(
    private readonly locationOneService: LocationOneService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    public exportService: ExportService,
  ) { }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  private initializeCreateLocation(): ICreateLocationOne {
    return { code: "", name: "" };
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: "ASC" | "DESC" = this.sort.direction === "asc" ? "ASC" : "DESC";

    const selectedStatusesIds = this?.selectedStatuses ? this.selectedStatuses.filter((status) => isNumber(status)) : [];

    this.locationOneService
      .findAllWithCount(
        { currentPage: pageIndex, limitPerPage: this.pageSize, search: this.searchQuery, sort: sortField, sortDirection: sortDirection },
        selectedStatusesIds
      )
      .subscribe((data) => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof LocationOneAttributes {
    const validSortFields: (keyof LocationOneAttributes)[] = ["code", "name", "statusId"];
    return validSortFields.includes(this.sort.active as keyof LocationOneAttributes) ? (this.sort.active as keyof LocationOneAttributes) : "code";
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount(); 
  }
  
  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch(){
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.paginator.pageIndex = 0;
          this.findAllWithCount();
        }
      });
  }

  trackByFn(index: number, item: LocationOneAttributes): string {
    return item._id.toString();
  }

  openCreateModal(): void {
    this.currentCreateLocation = this.initializeCreateLocation();
    this.dialogRefAdd = this.dialog.open(this.addModal, { panelClass: "form-modal", disableClose: true })
    // this.dialogRefAdd.afterClosed().subscribe((result) => result && this.createLocation());
    this.dialogRefAdd.afterClosed().subscribe((result) => { result });
  }
  
  createLocation(): void {
    this.locationOneService.create(this.currentCreateLocation).subscribe({
      next: () => {
        this.snackBar.open('Registro creado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
        this.dialogRefAdd.close()
      },
      error: (err) => {
        this.snackBar.open(err.error.message || 'Error al crear el registro', 'Cerrar', { duration: 3000 });
      }
    });
  }
  
  openEditModal(location: LocationOneAttributes): void {
    this.currentUpdateLocation = { ...location };
    this.dialogRefEdit = this.dialog.open(this.editModal, { panelClass: "form-modal", disableClose: true })
    // this.dialogRefEdit.afterClosed().subscribe((result) => result && this.updateLocation(location._id.toString()));
    this.dialogRefEdit.afterClosed().subscribe((result) => { result });
  }

  updateLocation(id: string): void {
    if (this.currentUpdateLocation) {
      const updatedLocation: IUpdateLocationOne = { ...this.currentUpdateLocation };

      this.locationOneService.update(id, updatedLocation).subscribe({
        next: () => {
          this.snackBar.open('Registro actualizado con éxito', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
          this.dialogRefEdit.close()
        },
        error: (err) => {
          this.snackBar.open(err.error.message || 'Error al actualizar el registro', 'Cerrar', { duration: 3000 });
        }
      });
    }
  }

  onStatusChange(event: boolean, location: LocationOneAttributes): void {
    const originalStatusId = location.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedLocation = { ...location, statusId: updatedStatusId };

    this.locationOneService.update(location._id.toString(), updatedLocation).subscribe({
      next: () => {
        this.findAllWithCount();
      },
      error: () => {
        location.statusId = originalStatusId; // Volver al estado original si falla
        this.snackBar.open('Error al actualizar el estado.', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
      }
    });
  }

  openDeleteDialog(location: LocationOneAttributes): void {
    this.dialog
      .open(this.deleteDialog, { data: location, panelClass: "delete-modal", disableClose:true })
      .afterClosed()
      .subscribe((result) => result && this.delete(location._id.toString()));
  }

  delete(id: string): void {
    this.locationOneService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
    });
  }

  applyStatusFilter(): void {
    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}

